import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { LabeledInput } from "../formPrimitives";

export interface ErreichbarkeitRufnummerItem {
  id: string;
  label: string;
  number: string;
  erreichbarkeit: string;
  onChange: (v: string) => void;
}

const useStyles = makeStyles({
  scrim: {
    position: "fixed",
    inset: 0,
    zIndex: 100002,
    backgroundColor: "rgba(20,8,20,0.38)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: REFINED.fontUI,
  },
  dialog: {
    width: "min(560px, 100%)",
    maxHeight: "90vh",
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("14px"),
    boxShadow: REFINED.cardShadow,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: `2px solid ${REFINED.goldLine}`,
  },
  title: {
    margin: 0,
    fontFamily: REFINED.fontUI,
    fontSize: "1.1rem",
    fontWeight: 700,
    color: REFINED.ink,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  closeBtn: {
    background: "none",
    ...shorthands.borderWidth("0"),
    fontSize: "1.2rem",
    cursor: "pointer",
    color: REFINED.ink3,
    ":hover": { color: REFINED.ink },
  },
  body: {
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    overflowY: "auto",
  },
  sectionTitle: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.86rem",
    fontWeight: 700,
    color: REFINED.ink,
    marginBottom: "4px",
  },
  hint: {
    fontSize: "0.82rem",
    color: REFINED.ink3,
    margin: 0,
  },
  foot: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "14px 20px",
    backgroundColor: REFINED.fieldBg,
    borderTop: `1px solid ${REFINED.line}`,
  },
  submitBtn: {
    ...shorthands.border("1px", "solid", REFINED.aub),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    padding: "8px 20px",
    cursor: "pointer",
    ":hover": { opacity: 0.92 },
  },
});

export const ErreichbarkeitModal: React.FC<{
  open: boolean;
  onClose: () => void;
  allgemeinValue: string;
  onAllgemeinChange: (v: string) => void;
  rufnummern: ErreichbarkeitRufnummerItem[];
}> = ({ open, onClose, allgemeinValue, onAllgemeinChange, rufnummern }) => {
  const s = useStyles();

  if (!open) return null;

  return createPortal(
    <div className={s.scrim} onClick={onClose}>
      <div className={s.dialog} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={s.head}>
          <h2 className={s.title}>
            <svg
              width="18"
              height="18"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="8" cy="8" r="6.2" />
              <polyline points="8 4.2 8 8 10.5 9.5" />
            </svg>
            Erreichbarkeit
          </h2>
          <button type="button" className={s.closeBtn} onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>

        <div className={s.body}>
          {/* Allgemeine Erreichbarkeit */}
          <div>
            <div className={s.sectionTitle}>Allgemeine Erreichbarkeit</div>
            <p className={s.hint} style={{ marginBottom: "8px" }}>
              Gilt grundsätzlich für diesen Kontakt (z. B. Kernarbeitszeiten oder Bürozeiten).
            </p>
            <LabeledInput
              label="Erreichbarkeitszeiten"
              name="allgemeineErreichbarkeit"
              value={allgemeinValue}
              onChange={onAllgemeinChange}
              placeholder="z. B. Mo–Do 8:00 – 17:00 Uhr, Fr bis 14:00 Uhr"
            />
          </div>

          <hr style={{ height: "1px", backgroundColor: REFINED.line, margin: "8px 0", borderWidth: 0 }} />

          {/* Erreichbarkeit je Rufnummer */}
          <div>
            <div className={s.sectionTitle}>Erreichbarkeit je Rufnummer</div>
            {rufnummern.length === 0 ? (
              <p className={s.hint}>
                Sobald eine Telefon- oder Mobilnummer eingetragen ist, kann hier eine nummernspezifische Erreichbarkeit hinterlegt werden.
              </p>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", rowGap: "14px", marginTop: "8px" }}>
                {rufnummern.map((r) => (
                  <div key={r.id}>
                    <LabeledInput
                      label={`${r.label} (${r.number})`}
                      name={`erreichbarkeit-${r.id}`}
                      value={r.erreichbarkeit}
                      onChange={r.onChange}
                      placeholder="z. B. Mo–Fr 9–12 Uhr, nur vormittags"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={s.foot}>
          <button type="button" className={s.submitBtn} onClick={onClose}>
            Fertig
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ErreichbarkeitModal;
